import type { FC, ReactNode } from 'react'
import type { BrowseFilters, Category, ColorCount } from '@/types'

import Link from 'next/link'
import { Layers, Monitor, Smartphone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buildBrowseHref, SORT_OPTIONS } from '@/lib/browse'
import { SearchForm } from '@/components/generals/search-form'
import { ColorFilter } from '@/components/wallpapers/color-swatches'

interface SegmentedOption {
    label: string
    href: string
    active: boolean
    icon?: ReactNode
}

interface SegmentedLinksProps {
    label: string
    options: Array<SegmentedOption>
}

const SegmentedLinks: FC<SegmentedLinksProps> = ({ label, options }): ReactNode => {
    return (
        <nav aria-label={label}>
            <ul className='inline-flex flex-wrap gap-1 rounded-xl border border-white/10 bg-white/[0.03] p-1'>
                {options.map(option => (
                    <li key={option.label}>
                        <Link
                            href={option.href}
                            scroll={false}
                            aria-current={option.active ? 'true' : undefined}
                            className={cn(
                                'inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                                option.active ? 'bg-white/[0.12] text-foreground' : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {option.icon}
                            {option.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    )
}

interface BrowseFilterBarProps {
    filters: BrowseFilters
    categories: Array<Category>
    colors: Array<ColorCount>
}

const chipClasses = 'inline-flex h-9 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-medium whitespace-nowrap transition focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'

export const BrowseFilterBar: FC<BrowseFilterBarProps> = ({ filters, categories, colors }): ReactNode => {
    const listedCategories = categories.filter(category => category.wallpaper_count > 0 || category.slug === filters.category)

    return (
        <div className='space-y-5'>
            <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
                <SearchForm
                    defaultValue={filters.q}
                    baseFilters={filters}
                    placeholder='Search by name, tag or mood'
                    className='lg:max-w-md'
                />

                <SegmentedLinks
                    label='Sort by'
                    options={SORT_OPTIONS.map(option => ({
                        label: option.label,
                        href: buildBrowseHref(filters, { sort: option.value }),
                        active: filters.sort === option.value
                    }))}
                />
            </div>

            {listedCategories.length > 0 && (
                <nav aria-label='Categories'>
                    <ul className='no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6 lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0'>
                        <li>
                            <Link
                                href={buildBrowseHref(filters, { category: '' })}
                                scroll={false}
                                aria-current={!filters.category ? 'true' : undefined}
                                className={cn(chipClasses, !filters.category ? 'border-transparent bg-primary text-primary-foreground' : 'border-white/10 text-foreground/80 hover:bg-white/[0.06]')}
                            >
                                All categories
                            </Link>
                        </li>

                        {listedCategories.map(category => {
                            const active = filters.category === category.slug

                            return (
                                <li key={category.id}>
                                    <Link
                                        href={buildBrowseHref(filters, { category: active ? '' : category.slug })}
                                        scroll={false}
                                        aria-current={active ? 'true' : undefined}
                                        className={cn(chipClasses, active ? 'border-transparent bg-primary text-primary-foreground' : 'border-white/10 text-foreground/80 hover:bg-white/[0.06]')}
                                    >
                                        <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                            {category.name}
                                        </span>

                                        <span className={cn('text-xs', active ? 'text-primary-foreground/60' : 'text-muted-foreground')}>
                                            {category.wallpaper_count}
                                        </span>
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>
            )}

            <div className='flex flex-wrap items-center gap-x-8 gap-y-4'>
                <SegmentedLinks
                    label='Device'
                    options={[
                        { label: 'All devices', href: buildBrowseHref(filters, { device: 'all' }), active: filters.device === 'all', icon: <Layers aria-hidden='true' className='size-4' /> },
                        { label: 'Desktop', href: buildBrowseHref(filters, { device: 'desktop' }), active: filters.device === 'desktop', icon: <Monitor aria-hidden='true' className='size-4' /> },
                        { label: 'Phone', href: buildBrowseHref(filters, { device: 'phone' }), active: filters.device === 'phone', icon: <Smartphone aria-hidden='true' className='size-4' /> }
                    ]}
                />

                {colors.length > 0 && (
                    <div className='flex flex-wrap items-center gap-3'>
                        <span className='text-sm font-medium text-muted-foreground'>
                            Color
                        </span>

                        <ColorFilter colors={colors} filters={filters} />
                    </div>
                )}
            </div>
        </div>
    )
}
